/**
 * Lista lateral: buscar, filtrar e escolher. É a navegação principal do painel —
 * o mapa é para enxergar, a lista é para achar.
 *
 * ## O IMEI saiu da linha em 28/10
 *
 * Ele era a segunda opção quando não havia placa, e ocupava a linha inteira com
 * quinze dígitos que ninguém lê numa lista. Quem precisa do IMEI está na tela
 * de Vínculos ou no detalhe do veículo — e a **busca continua aceitando** IMEI,
 * que é o uso real dele aqui.
 *
 * No lugar entraram seis indicadores em ícone, com a informação na dica. Ver
 * `VehicleIndicators.tsx`.
 *
 * ## E a placa saiu em 01/11
 *
 * Pelo mesmo motivo: ela não é mais o nome da linha e não respondia nada ali.
 * Quem despacha pergunta primeiro **de qual base** é o carro, e é isso que a
 * segunda linha mostra agora — com "Sem base" escrito quando não há, porque uma
 * linha vazia ali não distingue "ainda não cadastrado" de "não carregou". A
 * **busca continua aceitando placa e IMEI** — que é o uso real dos dois aqui —,
 * e passou a aceitar a base também: campo escrito na linha que a busca ignora
 * faz a tela dizer "nenhum veículo encontrado" com a palavra à vista.
 */
import { motion } from "motion/react";
import { getVehicleBase, getVehicleLabel, getVehicleStatus, isBacklog, matchesSearch, sortVehicles, STATUS, type VehicleStatus } from "../lib/fleet.ts";
import { formatDuration, formatRelative, formatSpeed } from "../lib/format.ts";
import type { FleetIndicator, Vehicle } from "../lib/schemas.ts";
import { Badge, Dot, EmptyState } from "./ui";
import { VehicleIndicators } from "./VehicleIndicators";

export function VehicleList({
  vehicles,
  search,
  statusFilter,
  selectedImei,
  recentlyChanged,
  indicators,
  onSearch,
  onSelect,
}: {
  vehicles: Vehicle[];
  search: string;
  statusFilter: VehicleStatus | null;
  selectedImei: string | null;
  recentlyChanged: Set<string>;
  /** Alerta pendente e motorista, por IMEI. Vem de `/fleet/indicators`. */
  indicators: Map<string, FleetIndicator>;
  onSearch: (term: string) => void;
  onSelect: (imei: string) => void;
}) {
  const visible = sortVehicles(
    vehicles.filter(
      (vehicle) => matchesSearch(vehicle, search) && (!statusFilter || getVehicleStatus(vehicle) === statusFilter),
    ),
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="p-3">
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Buscar por veículo, base, placa ou IMEI"
          className="w-full rounded-lg border border-line bg-panel/80 px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-accent focus:outline-none"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {visible.length === 0 ? (
          <EmptyState title="Nenhum veículo encontrado" hint="Ajuste a busca ou o filtro de status." />
        ) : (
          <ul className="space-y-1">
            {visible.map((vehicle) => (
              <VehicleRow
                key={vehicle.imei}
                vehicle={vehicle}
                selected={vehicle.imei === selectedImei}
                changed={recentlyChanged.has(vehicle.imei)}
                indicator={indicators.get(vehicle.imei)}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function VehicleRow({
  vehicle,
  selected,
  changed,
  indicator,
  onSelect,
}: {
  vehicle: Vehicle;
  selected: boolean;
  changed: boolean;
  indicator: FleetIndicator | undefined;
  onSelect: (imei: string) => void;
}) {
  const status = STATUS[getVehicleStatus(vehicle)];

  return (
    <li>
      <motion.button
        type="button"
        onClick={() => onSelect(vehicle.imei)}
        whileTap={{ scale: 0.99 }}
        // O realce verde marca "acabou de chegar dado deste". Sem ele, num
        // painel ao vivo não dá para saber se a tela está viva ou congelada.
        animate={changed ? { backgroundColor: ["rgba(52,211,153,0.16)", "rgba(0,0,0,0)"] } : {}}
        transition={{ duration: 1.4 }}
        className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
          selected ? "border-accent/60 bg-accent/10" : "border-transparent hover:bg-subtle/50"
        }`}
      >
        <div className="flex items-center gap-2">
          <Dot className={status.dot} />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
            {getVehicleLabel(vehicle)}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted">{formatSpeed(vehicle.speed)}</span>
        </div>

        {/* A BASE no lugar da placa (01/11). Quem opera filtra por base antes
            de filtrar por veículo — e a placa, que já não é o nome da linha,
            não respondia nenhuma pergunta aqui. Ela continua na seção de
            registro do detalhe e no cadastro. */}
        <div className="mt-1 flex items-center gap-2 pl-4 text-[11px] text-muted">
          {/* O texto sai de `getVehicleBase` nos dois casos — repetir a
              frase "Sem base" aqui criaria uma segunda verdade sobre ela. O
              que a ausência muda é só o estilo. */}
          <span className={`min-w-0 truncate ${vehicle.base?.trim() ? "" : "italic text-faint"}`}>
            {getVehicleBase(vehicle)}
          </span>
          <span className="ml-auto shrink-0">{formatRelative(vehicle.lastPacketAt)}</span>
        </div>

        <div className="mt-1.5 pl-3.5">
          <VehicleIndicators vehicle={vehicle} indicator={indicator} />
        </div>

        {isBacklog(vehicle) ? (
          <div className="mt-1.5 pl-4">
            <Badge className={status.badge}>
              Atraso de {formatDuration(vehicle.delaySeconds)} — posição não é a atual
            </Badge>
          </div>
        ) : null}
      </motion.button>
    </li>
  );
}
